/**
 * ODM Submission History — paginated list of every 837P batch submitted to
 * Ohio Medicaid, with filters and a deep-link to the per-batch lifecycle.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { formatDateTime } from '../lib/utils';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { StatsCardGrid } from '../components/shared/StatsCardGrid';
import ErrorReportsTab from './odm-error-reports/ErrorReportsTab';
import {
  PaperPlaneTilt,
  Stack,
  CurrencyDollar,
  CheckCircle,
  ArrowRight,
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  Warning,
} from '@phosphor-icons/react';

const STATUS_OPTIONS = [
  { value: 'All', label: 'All Statuses' },
  { value: 'Transmitted', label: 'Transmitted (awaiting ack)' },
  { value: 'Acknowledged', label: 'Acknowledged (999 received)' },
  { value: 'Accepted', label: 'Accepted (277CA)' },
  { value: 'Rejected', label: '277CA Rejected' },
  { value: '999 Rejected', label: '999 Rejected' },
  { value: 'Envelope Rejected', label: 'TA1 Envelope Rejected' },
  { value: 'Paid', label: 'Paid (835 received)' },
];

const STATUS_BADGE = {
  Transmitted: 'bg-slate-100 text-slate-700',
  Acknowledged: 'bg-blue-100 text-blue-800',
  Accepted: 'bg-emerald-100 text-emerald-800',
  Rejected: 'bg-red-100 text-red-800',
  '999 Rejected': 'bg-red-100 text-red-800',
  'Envelope Rejected': 'bg-red-100 text-red-800',
  Paid: 'bg-purple-100 text-purple-800',
};

const formatBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export default function OdmSubmissionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'error-reports' ? 'error-reports' : 'batches';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Keep ?tab=… in sync with the active tab so deep-links from toasts and
  // related-batch links land on the right view, and back/forward works.
  const handleTabChange = useCallback(
    (next) => {
      setActiveTab(next);
      setSearchParams(
        next === 'error-reports' ? { tab: 'error-reports' } : {},
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total_batches: 0, total_claims: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter && statusFilter !== 'All') params.status = statusFilter;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/odm-sftp/submissions/list', { params });
      setSubmissions(res.data.submissions || []);
      setStats(res.data.stats || { total_batches: 0, total_claims: 0 });
      setTotalPages(res.data.total_pages || 1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, search, dateFrom, dateTo]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const acceptedCount = useMemo(
    () => submissions.filter((s) => ['Accepted', 'Paid'].includes(s.response_status)).length,
    [submissions]
  );

  const statsCards = useMemo(
    () => [
      {
        label: 'Total Batches',
        value: stats.total_batches,
        icon: Stack,
        color: 'indigo',
        testid: 'stat-total-batches',
      },
      {
        label: 'Total Claims Submitted',
        value: stats.total_claims,
        icon: PaperPlaneTilt,
        color: 'sky',
        testid: 'stat-total-claims',
      },
      {
        label: 'Accepted on this page',
        value: acceptedCount,
        icon: CheckCircle,
        color: 'emerald',
        testid: 'stat-accepted-page',
      },
      {
        label: 'Currently Showing',
        value: submissions.length,
        icon: CurrencyDollar,
        color: 'slate',
        testid: 'stat-currently-showing',
      },
    ],
    [stats.total_batches, stats.total_claims, acceptedCount, submissions.length]
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('All');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="odm-submissions-page">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            <span>Submissions</span>
            <CaretRight size={10} />
            <span className="text-slate-700 font-medium">ODM History</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">ODM Submission History</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every 837P batch sent to Ohio Medicaid with the full ack/payment lifecycle.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList data-testid="odm-submissions-tabs">
            <TabsTrigger value="batches" data-testid="tab-batches">
              <Stack size={14} className="mr-2" />
              Batches
            </TabsTrigger>
            <TabsTrigger value="error-reports" data-testid="tab-error-reports">
              <Warning size={14} className="mr-2" />
              Error Reports
            </TabsTrigger>
          </TabsList>

          <TabsContent value="batches" className="space-y-6 mt-4">
            {/* Stats */}
            <StatsCardGrid columns={4} cards={statsCards} />

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <form onSubmit={handleSearch} className="md:col-span-4">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Search</label>
              <div className="relative">
                <MagnifyingGlass
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="filename or batch id"
                  className="pl-8"
                  data-testid="odm-history-search-input"
                />
              </div>
            </form>
            <div className="md:col-span-3">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Status</label>
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger data-testid="odm-history-status-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                data-testid="odm-history-date-from"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">To</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                data-testid="odm-history-date-to"
              />
            </div>
            <div className="md:col-span-1 flex items-end">
              <Button
                variant="outline"
                onClick={handleResetFilters}
                size="sm"
                className="w-full"
                data-testid="odm-history-reset-filters-btn"
              >
                Reset
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className="bg-white border border-slate-200 rounded-xl overflow-hidden"
          data-testid="odm-history-table-wrapper"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-600 tracking-wider">
                <tr>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Filename</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3 text-right">Claims</th>
                  <th className="px-4 py-3 text-right">Size</th>
                  <th className="px-4 py-3">ISA #</th>
                  <th className="px-4 py-3 text-right">Acks</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                      Loading submissions…
                    </td>
                  </tr>
                )}
                {!loading && submissions.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                      No submissions match these filters.
                    </td>
                  </tr>
                )}
                {!loading &&
                  submissions.map((s) => (
                    <tr
                      key={s.batch_id || s.filename}
                      onClick={() => navigate(`/odm/submissions/${encodeURIComponent(s.batch_id)}`)}
                      className="hover:bg-indigo-50/30 cursor-pointer"
                      data-testid={`odm-history-row-${s.batch_id}`}
                    >
                      <td className="px-4 py-3">
                        <Badge
                          className={`${STATUS_BADGE[s.response_status] || STATUS_BADGE.Transmitted} text-[10px]`}
                        >
                          {s.response_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700 max-w-[420px] truncate">
                        {s.filename}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {formatDateTime(s.submitted_at)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{s.claim_count}</td>
                      <td className="px-4 py-3 text-right text-slate-500">
                        {formatBytes(s.file_size)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        {s.isa_control_number || '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">
                        {s.response_count || 0}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ArrowRight size={14} className="text-slate-400 inline" />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between bg-slate-50">
              <span className="text-xs text-slate-600">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  data-testid="odm-history-prev-page-btn"
                >
                  <CaretLeft size={14} />
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  data-testid="odm-history-next-page-btn"
                >
                  Next
                  <CaretRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="error-reports" className="mt-4">
            <ErrorReportsTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
